import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone,
  ChevronDown,
  CheckCircle2,
  ShieldCheck,
  FileCheck,
  Database,
  Laptop,
  Mail,
  Lock,
  FolderKanban,
  UserRound,
  Building2,
  Briefcase,
  Landmark,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getPostsByTag } from "@/data/blog-posts";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

const LOCATIONS = [
  "Montclair, CA",
  "Rancho Cucamonga, CA",
  "Ontario, CA",
  "Upland, CA",
  "Claremont, CA",
  "Chino, CA",
  "Pomona, CA",
  "Inland Empire & Orange County",
];

const TITLE = "Tech Support for Financial Advisors | SimpleTech";
const DESCRIPTION =
  "Secure, compliant tech support for financial advisors. SEC/FINRA-aware IT, data protection & software support from SimpleTech. Call (909) 255-9112.";
const CANONICAL = canonical("/services/tech-support-for-financial-advisors");

const FAQS = [
  {
    q: "Are you familiar with SEC and FINRA cybersecurity requirements for financial advisors?",
    a: "Yes. SimpleTech Helper works with financial advisors throughout the Inland Empire, CA to implement the specific safeguards SEC and FINRA examiners look for — including encrypted storage, MFA, documented policies, access controls, and incident response procedures. We help you build a defensible security posture, not just a functioning one.",
  },
  {
    q: "Can you support the financial planning and CRM software our practice uses?",
    a: "Absolutely. SimpleTech Helper provides support for the major financial advisory platforms including Redtail, Wealthbox, eMoney, MoneyGuidePro, Orion, Riskalyze, and Morningstar. If your tools need to integrate with each other, we make that happen cleanly.",
  },
  {
    q: "How do you handle data security for client financial information?",
    a: "We implement layered protection — encrypted backups, multi-factor authentication, secure remote access, malware protection, and access controls — across every device and system your Inland Empire, CA practice uses. Client financial data is treated as the high-value, high-risk asset it is.",
  },
  {
    q: "Can you help with records retention requirements for registered advisors?",
    a: "Yes. SimpleTech Helper sets up document management systems with organized file structures, version control, and audit-ready records storage designed to meet the retention obligations that apply to SEC-registered and FINRA-affiliated advisors in the Inland Empire, CA.",
  },
  {
    q: "Do you offer ongoing IT support or one-time help?",
    a: "Both. SimpleTech Helper offers one-time project support and proactive ongoing managed IT plans for financial advisory practices in the Inland Empire, CA. With an ongoing plan, most issues are caught and resolved before they ever affect your clients or your compliance standing.",
  },
];

export const Route = createFileRoute("/services_/tech-support-for-financial-advisors")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Tech Support for Financial Advisors",
          description:
            "Secure, compliance-aware tech support for financial advisors and RIAs — including SEC/FINRA cybersecurity, financial planning software support, encrypted backup, and managed IT.",
          provider: {
            "@type": "LocalBusiness",
            name: "SimpleTech",
            telephone: "+1-909-255-9112",
            url: SITE_URL,
            address: {
              "@type": "PostalAddress",
              streetAddress: "8880 N Benson Ave #121",
              addressLocality: "Montclair",
              addressRegion: "CA",
              postalCode: "91763",
              addressCountry: "US",
            },
          },
          areaServed: [
            "Montclair, CA",
            "Rancho Cucamonga, CA",
            "Ontario, CA",
            "Upland, CA",
            "Claremont, CA",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
    ],
  }),
  component: FinancialAdvisorsTechSupportPage,
});

const SERVICES = [
  {
    Icon: ShieldCheck,
    title: "Cybersecurity & Regulatory Compliance Support",
    body: "SEC and FINRA cybersecurity requirements aren't optional — and they're increasingly specific. SimpleTech helps financial advisors in the Inland Empire, CA implement the safeguards regulators look for: encrypted data storage, multi-factor authentication, documented security policies, access controls, and incident response planning. We don't just protect your clients' data; we help you demonstrate that protection in writing when an exam comes around.",
  },
  {
    Icon: Database,
    title: "Financial Planning Software Support",
    body: "Whether your practice runs on Redtail, Wealthbox, eMoney, MoneyGuidePro, Orion, Riskalyze, or Morningstar, SimpleTech provides setup, integration, and troubleshooting support for the platforms that drive your practice. We keep your CRM connected to your planning tools, your data syncing cleanly, and your workflows running the way they should — so your team spends time on clients, not on software problems.",
  },
  {
    Icon: Lock,
    title: "Client Data Protection & Encrypted Backup",
    body: "Your clients trusted you with their most sensitive financial information. SimpleTech implements layered data protection including encrypted backups with tested recovery, secure file storage, multi-factor authentication across all access points, and malware and ransomware protection. Every Inland Empire, CA advisory practice we support operates with the confidence that client data is protected and recoverable — no matter what happens.",
  },
  {
    Icon: Laptop,
    title: "Device Management & Remote Work Setup",
    body: "Financial advisors aren't always at their desks. Whether you're meeting a client in Rancho Cucamonga, reviewing a portfolio in Ontario, or working from home in Upland, SimpleTech ensures your devices are properly configured, encrypted, and secured for remote access. We manage laptops, desktops, tablets, and mobile devices so your data stays protected wherever you work — and so every device meets your compliance requirements.",
  },
  {
    Icon: Mail,
    title: "Email Security & Encrypted Communications",
    body: "Email is where most financial advisory breaches begin. SimpleTech configures and supports Google Workspace and Microsoft 365 with enhanced security settings, spam and phishing protection, encrypted email for sensitive client communications, and calendar and contact synchronization across devices. Your client communications deserve the same protection you give their portfolios.",
  },
  {
    Icon: FileCheck,
    title: "Document Management & Records Retention",
    body: "Financial advisors have strict obligations around records retention under SEC Rule 17a-4 and equivalent FINRA rules. SimpleTech sets up document management systems that organize client files, enforce version control, and create audit-ready records storage — so your practice is never scrambling to produce documentation during a regulatory review. We serve advisory firms across the entire Inland Empire, CA.",
  },
  {
    Icon: FolderKanban,
    title: "Practice Management & Workflow Optimization",
    body: "Time spent fighting technology is time not spent on clients or growth. SimpleTech audits your current technology workflow and identifies the friction points — slow systems, manual processes, disconnected platforms — then builds a cleaner, faster operating environment. Many Inland Empire, CA advisors find that a focused technology refresh returns hours of productive time every week.",
  },
];

const WHY_POINTS = [
  "SEC and FINRA cybersecurity compliance expertise",
  "Same-day response — most issues resolved within hours",
  "Support for Redtail, Wealthbox, eMoney, Orion, and more",
  "Layered data protection for high-risk client information",
  "Plain-English explanations — no tech jargon",
  "A real local team that picks up the phone",
];

function FinancialAdvisorsTechSupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="services" />
      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section className="relative bg-[#0b0a3d] text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0b0a3d] via-[#141432] to-[#1e1e5a] opacity-95" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 md:py-28">
            <p className="text-sky-400 font-bold tracking-widest text-sm uppercase mb-4">
              Financial Advisor Tech Support
            </p>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Tech Support for Financial Advisors in the Inland Empire
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-bold text-sky-400">
              Secure. Compliant. Fiduciary-grade.
            </p>
            <p className="mt-8 text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
              Financial advisors carry more than investment portfolios — they carry their clients' financial futures,
              retirement plans, and deepest financial fears. The technology that supports that responsibility needs to be
              secure, compliant, and completely reliable. SimpleTech provides{" "}
              <strong className="text-white">dedicated tech support for financial advisors in the Inland Empire</strong>{" "}
              who need their technology to meet the same standard of care they deliver to clients.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                REQUEST SUPPORT
              </Link>
            </div>
            <p className="mt-5 text-sm text-white/70">Local support. Fast response. Compliance-aware.</p>
          </div>
        </section>

        {/* Why */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              The Technology Stakes Are Higher for Financial Advisors
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              Financial advisors in the Inland Empire, CA sit at the intersection of two things cybercriminals target
              most: sensitive personal data and money. Your client files contain Social Security numbers, account
              numbers, tax returns, estate plans, and detailed financial histories. Your systems are a high-value target —
              and regulators know it.
            </p>
            <p className="mt-4 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              The SEC's cybersecurity rules and FINRA's guidance on information security require registered advisors to
              implement and document specific safeguards. Many small and independent advisory practices in the Inland
              Empire, CA lack the internal IT expertise to build and maintain those safeguards properly — which is
              exactly where SimpleTech steps in.
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {WHY_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-800">
                  <CheckCircle2 className="w-5 h-5 text-sky-500 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Services grid */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Tech Support Services for Financial Advisors
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech covers the full technology stack that Inland Empire, CA financial advisory practices depend on
              — from cybersecurity and compliance infrastructure to the everyday software support that keeps your team
              productive.
            </p>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {SERVICES.map(({ Icon, title, body }) => (
                <div key={title} className="bg-white rounded-lg p-7 shadow-sm hover:shadow-md transition-shadow">
                  <Icon className="w-10 h-10 text-sky-500" strokeWidth={1.5} />
                  <h3 className="mt-5 text-xl font-extrabold text-[#0b0a3d]">{title}</h3>
                  <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-slate-700 text-[15px] leading-relaxed max-w-3xl space-y-3">
              <p>
                Looking for a complete IT partner? See our{" "}
                <Link to="/services/managed-it-services" className="text-sky-600 font-semibold hover:underline">
                  managed IT services
                </Link>{" "}
                for end-to-end coverage, or our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "cyber-security" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  cybersecurity services
                </Link>{" "}
                to protect client data and meet compliance requirements.
              </p>
              <p>
                Need a reliable phone system too? See our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "voip" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  VoIP solutions for financial advisory offices
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Who we support */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">Who We Support</h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech provides financial advisor tech support across the Inland Empire, CA for:
            </p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { Icon: UserRound, label: "Independent Registered Investment Advisors (RIAs)" },
                { Icon: Building2, label: "Broker-dealer affiliated advisors and representatives" },
                { Icon: Briefcase, label: "Fee-only financial planning practices" },
                { Icon: Landmark, label: "Wealth management offices and multi-advisor teams" },
                { Icon: ShieldCheck, label: "Insurance-licensed financial professionals" },
                { Icon: FileCheck, label: "Practice support staff and operations personnel" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-4 p-5 rounded-md border border-slate-200 bg-slate-50">
                  <Icon className="w-8 h-8 text-sky-500 shrink-0" strokeWidth={1.5} />
                  <span className="font-semibold text-[#0b0a3d]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Locations We Serve */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">Locations We Serve</h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech supports financial advisory practices across Southern California — with same-day remote help
              everywhere we work, and on-site visits across the areas below.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {LOCATIONS.map((loc) => (
                <div
                  key={loc}
                  className="flex items-center gap-3 bg-white rounded-md border border-slate-200 px-4 py-3"
                >
                  <MapPin className="w-5 h-5 text-sky-500 shrink-0" />
                  <span className="font-semibold text-[#0b0a3d] text-sm">{loc}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-slate-700 leading-relaxed max-w-3xl">
              Don't see your city?{" "}
              <a href="tel:+19092559112" className="text-sky-600 font-semibold hover:underline">
                Call (909) 255-9112
              </a>{" "}
              — we likely cover it too.
            </p>
          </div>
        </section>

        {/* From the Blog */}
        <BlogSection />

        {/* CTA band */}
        <section className="bg-[#0b0a3d] text-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              Technology That Meets Your Standard of Care
            </h2>
            <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
              You hold yourself to the fiduciary standard with every client. Your technology partner should hold itself
              to the same standard with you. SimpleTech delivers fast, reliable, compliance-aware tech support for
              financial advisors in the Inland Empire — with local expertise, honest advice, and a team that picks up
              the phone.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                CONTACT US
              </Link>
            </div>
            <p className="mt-6 text-sm text-white/70">No rushing. No jargon. A team that answers the phone.</p>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight text-center">
              Frequently Asked Questions
            </h2>
            <div className="mt-12 space-y-3">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className="border border-slate-200 rounded-md overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50 transition-colors"
                      aria-expanded={open}
                    >
                      <span className="font-bold text-[#0b0a3d]">{f.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-sky-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    {open && <div className="px-5 pb-5 text-slate-700 leading-relaxed">{f.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function BlogSection() {
  const posts = getPostsByTag("financial");
  if (posts.length === 0) return null;
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Financial Advisor Tech Insights
            </h2>
            <p className="mt-4 text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
              Guides, tips, and updates for financial advisors and advisory practices — from our blog.
            </p>
          </div>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-sky-600 font-bold hover:text-sky-700"
          >
            All articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.slice(0, 6).map((post) => (
            <Link
              key={post.slug}
              to="/articles/$slug"
              params={{ slug: post.slug }}
              className="group bg-white rounded-lg overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold text-sky-600 tracking-wider uppercase">{post.date}</p>
                <h3 className="mt-2 text-lg font-extrabold text-[#0b0a3d] group-hover:text-sky-600 transition-colors leading-tight">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
